import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import UploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography
} from '@mui/material';
import { useRef, useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { callAction, dataProvider } from './dataProvider';
import { messageFor } from './upload';
import { FileImage } from './image';

/**
 * A product's pictures.
 *
 * Three of them, because that is what the storefront renders - the first as
 * the main image in listings and on the product page, the other two as
 * thumbnails beside it. The `product_gallery_image` table exists but no
 * template reads it, so an unlimited gallery here would let somebody upload
 * pictures the shop never shows.
 *
 * Saved immediately rather than with the rest of the form. Uploading a picture
 * and then losing it to an unsaved form is a worse surprise than the extra
 * request, and the order of the slots is itself the "which is the main one"
 * decision, so it needs to survive leaving the tab.
 */

const MAX_BYTES = 10 * 1024 * 1024;
/*
 * Both the media types and the extensions.
 *
 * The extensions matter: a picker filtering on media type alone greys out
 * files whose type the operating system does not know - .jfif being the common
 * one, which is what Windows produces from "save image as". The server decides
 * either way; this only governs what is selectable.
 */
const ACCEPTED = [
  'image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/avif',
  '.jpg', '.jpeg', '.jpe', '.jfif', '.png', '.webp', '.gif', '.avif'
].join(',');
const SLOTS = 3;

interface Product {
  id: number | string;
  fileIdentifier?: string | null;
  fileIdentifier2?: string | null;
  fileIdentifier3?: string | null;
}

interface UploadInstruction {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
}

export function ProductImages() {
  const record = useRecordContext<Product>();
  const notify = useNotify();
  const refresh = useRefresh();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  if (record == null) return null;

  const images = [record.fileIdentifier, record.fileIdentifier2, record.fileIdentifier3]
    .filter((id): id is string => id != null && id !== '');

  const save = async (next: (string | null)[], message: string) => {
    setBusy(true);
    try {
      await dataProvider.update('products', {
        id: record.id,
        data: { fileIdentifiers: next },
        previousData: record
      });
      notify(message, { type: 'success' });
      refresh();
    } catch {
      notify('Obrázky sa nepodarilo uložiť.', { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File) => {
    if (file.size > MAX_BYTES) {
      notify(`Súbor má ${(file.size / 1024 / 1024).toFixed(1)} MB, maximum je 10 MB.`, { type: 'warning' });
      return;
    }

    setBusy(true);
    try {
      const instruction = (await callAction('uploads', 'POST', {
        contentType: file.type,
        filename: file.name
      })) as UploadInstruction;

      const response = await fetch(instruction.url, {
        method: instruction.method,
        headers: instruction.headers,
        body: file
      });
      if (!response.ok) throw new Error(String(response.status));

      await save([...images, instruction.id], 'Obrázok pridaný.');
    } catch (error) {
      console.error('Upload failed', error);
      notify(messageFor(error), { type: 'warning' });
      setBusy(false);
    } finally {
      if (input.current != null) input.current.value = '';
    }
  };

  const remove = (id: string) => void save(images.filter(other => other !== id), 'Obrázok odstránený.');

  // Making an image the main one is just moving it to the front; the rest keep
  // their order, so it is not a swap that shuffles the other two around.
  const makeDefault = (id: string) =>
    void save([id, ...images.filter(other => other !== id)], 'Hlavný obrázok zmenený.');

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.secondary">Obrázky</Typography>
        {busy && <CircularProgress size={14} />}
      </Stack>

      <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        {images.map((id, index) => (
          <Box key={id} sx={{ position: 'relative' }}>
            <Box
              sx={{
                p: 0.5,
                borderRadius: 1,
                border: '2px solid',
                borderColor: index === 0 ? 'primary.main' : 'divider'
              }}
            >
              <FileImage id={id} size={110} alt="" />
            </Box>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
              {index === 0 ? (
                <Chip size="small" color="primary" icon={<StarIcon />} label="Hlavný" />
              ) : (
                <Tooltip title="Nastaviť ako hlavný obrázok">
                  <IconButton size="small" disabled={busy} onClick={() => makeDefault(id)}>
                    <StarBorderIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title="Odstrániť z produktu">
                <IconButton size="small" disabled={busy} onClick={() => remove(id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        ))}

        {images.length < SLOTS && (
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            disabled={busy}
            onClick={() => input.current?.click()}
            sx={{ height: 122, width: 122, borderStyle: 'dashed' }}
          >
            Pridať
          </Button>
        )}
      </Stack>

      {images.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          Produkt nemá obrázok — v zoznamoch sa zobrazí bez neho.
        </Typography>
      )}

      {images.length >= SLOTS && (
        <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
          Web zobrazuje najviac tri obrázky. Ak chcete pridať ďalší, najprv niektorý odstráňte.
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
        Prvý obrázok je hlavný — zobrazuje sa v zoznamoch aj na stránke produktu. Zmeny sa ukladajú
        okamžite.
      </Typography>

      <input
        ref={input}
        type="file"
        accept={ACCEPTED}
        hidden
        onChange={event => {
          const file = event.target.files?.[0];
          if (file != null) void upload(file);
        }}
      />
    </Box>
  );
}
